import PropTypes from 'prop-types';
import React from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import * as Table from 'reactabular-table';
import * as edit from 'react-edit';
import { cloneDeep, each, map, trimStart } from 'lodash';
import Validate from './Validate';
import Headers from './ODTableHeaders';
import PriceInput from './PriceInput';
import CitySearchCell from './CitySearchCell';


const dataWithTariffRate = (tariffRate) => {
    const data = {};
    data.rows = tariffRate.od_pairs;
    return data;
};

class OriginDestinationTable extends React.Component {
    static propTypes = {
        tariffRate: PropTypes.object,
        onChange: PropTypes.func,
    };

    constructor(props) {
        super(props);
        const formatters = {
            fixedFloat: value => parseFloat(value).toFixed(2),
            fixedInt: value => parseInt(value),
        };
        const validators = {
            prices: value => Validate(value => (value > 0))(value),
        };

        this.state = {
            tariffRate: {},
            formatters: formatters,
            data: { rows: [] },
            editedCell: null,
            editedKey: null,
            validators: validators,
        };
    }
    componentDidMount() {
        const tariffRate = this.props.tariffRate;
        let data = this.state.data;
        if (tariffRate) {
            data = dataWithTariffRate(tariffRate);
            this.setState({ data: data });
        }
    };

    onChangePrices = (changedProperty) => {
        if (this.props.onChange) {
            this.props.onChange(changedProperty);
        }
    };

    createRow = (index) => {
        const dataRows = this.state.data.rows || [];
        let newRow = {};
        if (dataRows.length && index > 0 && index <= dataRows.length) {
            const lastRow = dataRows[index - 1];
            newRow = JSON.parse(JSON.stringify(lastRow));
        } else {
            const values = [];
            each(Headers, (header, index) => {
                values.push({
                    name: header.value,
                    value: 0,
                });
            });
            newRow = {
                origin_city: {
                    city: '',
                    city_code: '',
                    state: '',
                },
                destination_city: {
                    city: '',
                    city_code: '',
                    state: '',
                },
                values: values,
            };
        }
        return newRow;
    };

    addRow = () => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        const index = dataRows.length;
        const newRow = this.createRow(index);
        dataRows.push(newRow);
        data.rows = dataRows;
        this.setState({ data: data });
        this.onChangePrices({ od_pairs: data.rows });
    };

    insertRow = (index) => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        const newRow = this.createRow(index);
        if (dataRows.length && index <= dataRows.length) {
            dataRows.splice(index, 0, newRow);
            data.rows = dataRows;
            this.setState({ data: data });
            this.onChangePrices({ od_pairs: data.rows });
        }
    };

    removeRow = (index) => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        if (dataRows.length && index < dataRows.length) {
            dataRows.splice(index, 1);
            data.rows = dataRows;
            this.setState({ data: data });
            this.onChangePrices({ od_pairs: data.rows });
        }
    };

    tableData = () => {
        const data = this.state.data;
        const tableData = map(data.rows, function (item) {
            const row = {
                origin_city: item.origin_city,
                destination_city: item.destination_city,
                editing: item.editing,
            };
            each(item.values, function (value, index) {
                const valueKey = `Value${index + 1}`;
                row[valueKey] = value.value;
            });
            return row;
        });
        return tableData;
    };

    tableColumns = () => {
        const formatters = (this.state) ? this.state.formatters : {};
        const data = this.state.data;
        const validators = this.state.validators;
        const columns = [];
        if (data.rows) {
            const context = this;
            const citySearch = edit.edit({
                isEditing: ({ columnIndex, rowData }) => columnIndex === rowData.editing,
                onActivate: ({ columnIndex, rowIndex }) => {
                    const dataClone = cloneDeep(this.state.data);
                    dataClone.rows[rowIndex].editing = columnIndex;
                    this.setState({ data: dataClone });
                },
                onValue: ({ value, rowIndex, property }) => {
                    const dataClone = cloneDeep(this.state.data);
                    const row = dataClone.rows[rowIndex];
                    const updatedValue = value;
                    const componentForm = {
                        locality: ['long_name', 'city'],
                        administrative_area_level_1: ['short_name', 'state'],
                    };
                    for (let i = 0; i < updatedValue.gmaps.address_components.length; i++) {
                        const addressType = updatedValue.gmaps.address_components[i].types[0];
                        if (componentForm[addressType]) {
                            const val = updatedValue.gmaps.address_components[i][componentForm[addressType][0]];
                            row[property][componentForm[addressType][1]] = val;
                        }
                    }
                    row.editing = false;
                    context.setState({ data: dataClone, editedKey: null });
                    context.onChangePrices({ od_pairs: dataClone.rows });
                },
            });
            columns.push({
                property: 'origin_city',
                header: {
                    label: 'Origin City',
                },
                cell: {
                    formatters: [
                        value => (`${value.city}, ${value.state}`),
                    ],
                    transforms: [
                        citySearch(CitySearchCell()),
                    ],
                },
            });
            columns.push({
                property: 'destination_city',
                header: {
                    label: 'Destination City',
                },
                cell: {
                    formatters: [
                        value => (`${value.city}, ${value.state}`),
                    ],
                    transforms: [
                        citySearch(CitySearchCell()),
                    ],
                },
            });
            const dataEditable = edit.edit({
                isEditing: ({ columnIndex, rowData }) => columnIndex === rowData.editing,
                onActivate: ({ columnIndex, rowIndex }) => {
                    const dataClone = cloneDeep(this.state.data);
                    dataClone.rows[rowIndex].editing = columnIndex;
                    this.setState({ data: dataClone });
                },
                onValue: ({ value, rowIndex, property }) => {
                    const dataClone = cloneDeep(this.state.data);
                    const row = dataClone.rows[rowIndex];
                    const updatedValue = parseFloat(value);
                    const valueIndex = parseInt(trimStart(property, 'Value')) - 1;
                    row.values[valueIndex].value = updatedValue;
                    row.editing = false;
                    context.setState({ data: dataClone, editedKey: null });
                    context.onChangePrices({ od_pairs: dataClone.rows });
                },
            });

            each(Headers, (header, index) => {
                const columnHeader = (<div>{header.name}</div>);
                columns.push({
                    property: `Value${index + 1}`,
                    header: {
                        label: columnHeader,
                    },
                    cell: {
                        formatters: [
                            formatters[header.formatter],
                            validators.prices,
                        ],
                        transforms: [
                            dataEditable(PriceInput()),
                        ],
                    },
                });
            });

            columns.push({
                header: {
                    label: (<div>Operation</div>),
                },
                cell: {
                    formatters: [
                        (value, { rowIndex }) => {
                            const remove = () => {
                                this.removeRow(rowIndex);
                            };
                            const insert = () => {
                                const nextIndex = rowIndex + 1;
                                this.insertRow(nextIndex);
                            };

                            return (
                              <div>
                                <ButtonGroup bsSize="xsmall">
                                  <Button
                                    className="btn btn-success"
                                    onClick={insert}
                                  >&#10010;
                                                </Button>
                                  <Button
                                    className="btn btn-danger"
                                    onClick={remove}
                                  >&#10006;
                                                </Button>
                                </ButtonGroup>
                              </div>
                            );
                        },
                    ],
                },
            });
        }
        return columns;
    };

    render() {
        const columns = this.tableColumns();
        const tableData = this.tableData();
        return (
          <Table.Provider
            className="default-table"
            columns={columns}
          >
            <Table.Header />
            <Table.Body rows={tableData} rowKey={({ rowIndex }) => `${rowIndex}price-table`} />
            <tfoot>
              <tr>
                {columns.map((column, i) => {
                    if (i === 0) {
                        return (<td key={`${i}-footer`}>
                          <Button
                            bsSize="xsmall"
                            className="btn btn-default"
                            onClick={this.addRow}
                          >Add Range Row
                                                </Button>
                        </td>);
                    }
                    return (<td key={`${i}-footer`} />);
                })}
              </tr>
            </tfoot>
          </Table.Provider >
        );
    }
}

export default OriginDestinationTable;
